/**
 * Provider stack: runtime config → QueryClient → IntlProvider → MotionProvider (console defaults calm-leaning
 * but respects the operator's own preference, D14) → LiveRegion/Toast → Session → StepUp → Router.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { IntlProvider, LiveRegionProvider, MotionProvider, ToastProvider } from '@windrop/ui';
import { useMemo, type ReactNode } from 'react';
import { RouterProvider } from 'react-router';
import { SessionProvider } from './auth/session';
import { StepUpProvider } from './auth/step-up';
import { ErrorBoundary } from './components/ErrorBoundary';
import { isApiError } from './lib/api/client';
import { readRuntimeConfig } from './lib/runtime-config';
import { createAppRouter } from './router';

export const createConsoleQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: (count, error) => {
          if (isApiError(error) && error.status < 500) return false;
          return count < 2;
        },
      },
      mutations: { retry: false },
    },
  });

/** Everything an app or a test needs around a React tree, minus the router. */
export function ConsoleProviders({
  children,
  queryClient,
}: {
  children: ReactNode;
  queryClient?: QueryClient;
}) {
  const client = useMemo(() => queryClient ?? createConsoleQueryClient(), [queryClient]);
  readRuntimeConfig();
  return (
    <ErrorBoundary scope="the console">
      <QueryClientProvider client={client}>
        <IntlProvider
          locale={typeof navigator !== 'undefined' ? navigator.language : 'en'}
          timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}
        >
          {/* Calm-leaning: main.tsx seeds the stored preference to CALM once; the operator's own choice always wins (D14). */}
          <MotionProvider>
            <LiveRegionProvider>
              <ToastProvider>
                <SessionProvider>
                  <StepUpProvider>{children}</StepUpProvider>
                </SessionProvider>
              </ToastProvider>
            </LiveRegionProvider>
          </MotionProvider>
        </IntlProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

const router = typeof window !== 'undefined' ? createAppRouter() : null;

export function App() {
  return <ConsoleProviders>{router ? <RouterProvider router={router} /> : null}</ConsoleProviders>;
}
